const create = {
  Title: 'Create New Caregiving Job',
  Subtitle: 'Fill out the information below to create a new caregiving position',
  Steps: {
    Details: 'Details',
    Schedule: 'Schedule',
    Price: 'Price',
    Location: 'Location',
    StartDate: 'Start Date',
    CarePlan: 'Care Plan',
    Requirements: 'Requirements',
    Considerations: 'Considerations',
    Review: 'Review',
  },
  Actions: {
    Back: 'Back',
    Continue: 'Continue',
    CreateJob: 'Create Job',
    BackToListing: 'Back to Job Listing',
  },
  Details: {
    Title: 'Job Description',
    ClientLabel: 'Client',
    ClientPlaceholder: 'Search and select a client',
    ClientExistsLabel: 'Client exists in system',
    UnregisteredClientLabel: 'Unregistered client',
    UnregisteredClientPlaceholder: 'Enter client name',
    ShortDescriptionLabel: 'Short Description',
    ShortDescriptionPlaceholder: 'Brief summary of the role (will be shown in job listings)',
    LongDescriptionLabel: 'Detailed Description',
    LongDescriptionPlaceholder: 'Provide a detailed description of the role, responsibilities, and any important information caregivers should know.',
    AboutRoleLabel: 'About this role',
  },
  Schedule: {
    Title: 'Set your schedule',
    Description: 'When do you need caregiving support? Choose your schedule type and configure the details.',
    ScheduleTypeLabel: 'Schedule Type',
    ScheduleType: {
      RECURRENCE_EVENT: 'Recurring Schedule',
      LIVEIN_EVENT: 'Live-in Schedule',
    },
    ScheduleTypes: {
      RECURRENCE_EVENT: 'Recurring Schedule',
      LIVEIN_EVENT: 'Live-in Schedule',
    },
    StartTimeLabel: 'Start Time',
    EndTimeLabel: 'End Time',
    WeeklySchedule: 'Weekly schedule',
    DayLabel: 'Day',
    AddTimeSlot: 'Add another time slot',
    Day: {
      SU: 'Sunday',
      MO: 'Monday',
      TU: 'Tuesday',
      WE: 'Wednesday',
      TH: 'Thursday',
      FR: 'Friday',
      SA: 'Saturday',
    },
    LiveInNote: 'Live-in care is available every day of the week.',
  },
  Price: {
    Title: 'Set your hourly rate',
    Subtitle: 'Choose a competitive rate that reflects the value of quality caregiving in your area.',
    HourlyRateLabel: 'Hourly rate',
    HourlyRateDescription: 'This is what you\'ll pay per hour for caregiving services.',
    WeeklyEstimateTitle: 'Weekly earnings estimate',
    WeeklyEstimateDefault: 'Enter an hourly rate to see weekly estimate',
    WeeklyEstimateCalculation: 'Based on #{hours} hours × $#{rate}/hr',
    WeeklyEarningsDisplay: '$#{amount}/week',
  },
  StartDate: {
    Title: 'When do you need care to start?',
    StartDate: 'Start Date',
    Subtitle: 'Let caregivers know your timeline so they can plan accordingly.',
    Options: {
      IMMEDIATELY: {
        Label: 'Immediately',
        Description: 'Ready to start right away',
      },
      ONEWEEK: {
        Label: 'Within 1 week',
        Description: 'Can begin in the next few days',
      },
      TWOWEEK: {
        Label: 'Within 2 weeks',
        Description: 'Need a bit more time to prepare',
      },
      ONEMONTH: {
        Label: 'Within 1 month',
        Description: 'Flexible timing, no rush',
      },
      TWOMONTH: {
        Label: 'Within 2 months',
        Description: 'Planning ahead for future needs',
      },
      FLEXIBLE: {
        Label: 'Flexible timing',
        Description: 'Open to discussing the best start date',
      },
    },
  },
  Location: {
    Title: 'Where is care needed?',
    Subtitle: 'Help caregivers understand the location and commute for this position.',
    NeighborhoodLabel: 'Neighborhood',
    NeighborhoodDescription: 'The general area where care will be provided',
    NeighborhoodPlaceholder: 'e.g., Roncesvalles',
    CityLabel: 'City',
    CityDescription: 'The city where care will be provided',
    CityPlaceholder: 'e.g., Toronto',
    LocationPreviewTitle: 'Location preview',
    LocationPreviewDescription: 'Caregivers will see: #{neighborhood}, #{city}',
    LocationPreviewWithLocation: 'Caregivers will see: #{location}',
    LocationPreviewNoLocation: 'Caregivers will see: (location not specified)',
  },
  CarePlan: {
    Title: 'What type of care is needed?',
    Subtitle: 'Select all the types of support that will be part of this caregiving role.',
    CareNeedsLabel: 'Care needs (select all that apply)',
    CareNeedsDescription: 'This helps caregivers understand what the role involves and whether they\'re a good fit.',
    SelectedCareNeedsTitle: 'Selected care needs',
    SelectedCareNeedsCount: 'Selected care needs (#{count})',
    Options: {
      MOBILITY_SUPPORT: {
        Label: 'Mobility support',
        Description: 'Help with walking, transfers, or getting around',
      },
      MEDICATION_REMINDERS: {
        Label: 'Medication reminders',
        Description: 'Ensuring medications are taken on time',
      },
      MEAL_PREP: {
        Label: 'Meal prep',
        Description: 'Preparing nutritious meals and snacks',
      },
      LIGHT_HOUSEKEEPING: {
        Label: 'Light housekeeping',
        Description: 'Basic cleaning and tidying up',
      },
      TRANSPORTATION: {
        Label: 'Transportation',
        Description: 'Driving to appointments and errands',
      },
      COMPANIONSHIP: {
        Label: 'Companionship',
        Description: 'Social interaction and emotional support',
      },
      PERSONAL_CARE: {
        Label: 'Personal care',
        Description: 'Assistance with bathing, grooming, and dressing',
      },
      MEDICAL_APPOINTMENTS: {
        Label: 'Medical appointments',
        Description: 'Accompanying to and from medical visits',
      },
      SHOPPING_ASSISTANCE: {
        Label: 'Shopping assistance',
        Description: 'Help with grocery shopping and errands',
      },
      PET_CARE: {
        Label: 'Pet care',
        Description: 'Feeding, walking, and caring for pets',
      },
    },
  },
  Requirements: {
    Title: 'What are your requirements?',
    Subtitle: 'Specify any certifications, licenses, or language requirements for this position.',
    CertificationsLabel: 'License or certification required',
    CertificationsDescription: 'Select the licenses and certifications for this role.',
    LanguagesLabel: 'Languages',
    LanguagesDescription: 'Select the languages the caregiver should speak',
    SelectedRequirementsTitle: 'Requirements summary',
    CertificationLabel: 'Certifications',
    LanguagesDisplayLabel: 'Languages',
    ColorKeyMandatory: 'Mandatory',
    ColorKeyAtLeastOne: 'At Least One Required',
    ColorKeyNiceToHave: 'Nice to Have',
    MandatoryCertifications: 'Mandatory Certifications',
    AtLeastOneRequired: 'At Least One Required',
    NiceToHave: 'Nice to Have',
    CertificationsOptions: {
      PSW: 'Personal Support Worker (PSW)',
      DSW: 'Developmental Service Worker (DSW)',
      PAB: 'Préposé aux bénéficiaires (PAB)',
      CNA: 'Certified Nursing Assistant (CNA)',
      HCA: 'Health Care Aide (HCA)',
      RNA: 'Registered Nursing Assistant (RNA)',
      RPN: 'Registered Practical Nurse (RPN)',
      RN: 'Registered Nurse (RN)',
      CPR: 'CPR Certification',
      FIRST_AID: 'First Aid Certification',
      PSW_STUDENT: 'PSW Student',
      NURSING_STUDENT: 'Nursing Student',
    },
    LanguageOptions: {
      ENGLISH: 'English',
      FRENCH: 'French',
      CHINESE: 'Chinese',
      HINDUSTANI: 'Hindustani',
      SPANISH: 'Spanish',
      PUNJABI: 'Punjabi',
      ARABIC: 'Arabic',
      TAGALOG: 'Tagalog',
      ITALIAN: 'Italian',
      GERMAN: 'German',
      PORTUGUESE: 'Portuguese',
      PERSIAN: 'Persian',
      RUSSIAN: 'Russian',
      TAMIL: 'Tamil',
      VIETNAMESE: 'Vietnamese',
      GUJARATI: 'Gujarati',
      POLISH: 'Polish',
      KOREAN: 'Korean',
      SERBO_CROATIAN: 'Serbo-Croatian',
      GREEK: 'Greek',
      HAITIAN_CREOLE: 'Haitian Creole',
      UKRAINIAN: 'Ukrainian',
      BENGALI: 'Bengali',
      ROMANIAN: 'Romanian',
      DUTCH: 'Dutch',
      CREE: 'Cree',
      JAPANESE: 'Japanese',
      HEBREW: 'Hebrew',
      TURKISH: 'Turkish',
      MALAYALAM: 'Malayalam',
      LITHUANIAN: 'Lithuanian',
      ILOCANO: 'Ilocano',
      SOMALI: 'Somali',
      SWAHILI: 'Swahili',
      TELUGU: 'Telugu',
      MANDARIN: 'Mandarin',
      CANTONESE: 'Cantonese',
      HINDI: 'Hindi',
    },
  },
  Considerations: {
    Title: 'Any special considerations?',
    Considerations: 'Considerations',
    Subtitle: 'Share any important details about the household, environment, or role that caregivers should know about.',
    CommonConsiderationsLabel: 'Common considerations (select all that apply)',
    Options: {
      COMFORTABLE_WITH_PETS: 'Comfortable working around pets',
      COMFORTABLE_WITH_STAIRS: 'Comfortable with stairs',
      NON_SMOKING_HOUSEHOLD: 'Non-smoking household preferred',
      PHYSICAL_LIFTING_REQUIRED: 'Physical lifting may be required',
      DEMENTIA_ALZHEIMER_EXPERIENCE: 'Experience with dementia/Alzheimer\'s',
      FLEXIBLE_SCHEDULE_CHANGES: 'Flexible with schedule changes',
    },
    AdditionalConsiderationsLabel: 'Additional considerations',
    AdditionalConsiderationsPlaceholder: 'Add any other important details about the household, environment, or specific needs.',
    SelectedConsiderationsTitle: 'Considerations preview',
    SelectedLabel: 'Selected',
    NoConsiderationsSelected: 'No considerations selected',
  },
  Review: {
    Title: 'Review',
    AboutThisRole: 'About this role',
    ClientFor: 'Client for this position',
    Description: 'Description',
    Compensation: 'Compensation',
    FlexibleStartTiming: 'Flexible start timing',
    CareNeeds: 'Care needs',
    SpecialConsiderations: 'Special considerations',
    AdditionalConsiderations: 'Additional considerations',
    NoSpecificRequirements: 'No specific requirements',
    NoSpecialConsiderations: 'No special considerations',
    WeeklyEarnings: '$#{amount}/week',
    BasedOnHours: 'Based on #{hours} hours/week',
    WeeklyHours: '#{hours} hours/week',
    NoScheduleSet: 'No schedule set',
    InvalidSchedule: 'Invalid schedule',
    InvalidScheduleType: 'Invalid schedule type',
    ZeroHoursPerWeek: '0 hours/week',
    HourlyRate: '$#{rate}/hr',
    LocationDisplay: '#{neighborhood}, #{city}',

    // Draggable attribute ordering
    OrderAttributes: 'Job Information Priority',
    OrderAttributesHelp: 'The top 3 will become interview questions.',

    // Caregiver notification selection
    NotifyCaregivers: 'Notify caregivers',
    SelectCaregivers: 'Select caregivers to notify about this job posting',
    NotifyCaregiversHelp: 'Selected caregivers will receive a notification about this job posting when it is created.',

  },
};

export default create;
